interface IFilterDepencency
{
    property: string;
    filterProperty: string;
    required: boolean;
    isArray: boolean;
    crossReference: boolean;
}
interface IFilterConfig
{
    property: string;
    value: any;
    path: string[];
}
interface ISiteLabelDropdownConfig
    extends ISiteLabelConfig {
    
    filters: IFilterConfig[];
}

class SiteLabelDropdown 
    extends SiteLabel<string, ISiteLabelDropdownConfig> {

    private readonly _dependencies: Array<IFilterDepencency> | null;
    private readonly _clickEvent?: (data: QueryEventedModel<ISiteHelperQueryRequestItem>) => Promise<void>;
    private readonly _changedEvent?: (data: ISiteModelValueChanged) => void;
    private readonly _input: IQueryElement;
    private readonly _dropdown: IQueryElement;
    private readonly _buttons: IQueryElement;
    private readonly _valueElement: IQueryElement;
    private readonly _results: ISiteHelperQuery;
    private readonly _valueTemplate: QueryTemplate;
    private readonly _editButton: SiteActionButton;
    private _value: string | null = null;

    constructor(card: SiteCard, model: ISiteModel, modelData: ISiteModel, element: HTMLElement) {
        super(card, model, modelData, element);

        let input = this.query("input").first();
        let query = input && input.getAttr("data-query") || "";
        let queryFilter = input && input.getAttr("data-query-filter");
        let dependencies = input && input.getAttr("data-dependencies");
        
        let dropdown = this.query(".dropdown").first();
        let buttons = this.query(".buttons").first();
        let value = this.query(".value").first();
        let metaData = this.getModelData();

        let filter:any = queryFilter && { $type: queryFilter } || {};
        for (let item of this.config.filters)
        {
            filter[item.property] = item.value;
        }

        let results = this.query(".results").create<ISiteHelperQuery, HTMLElement>(a =>
            new SiteHelperQuery2d(a, {
                refreshTime: 0,
                query: query,
                filter: filter,
                disableDoubleClickOnSelect: false
            })
        );
        
        if(input === null || buttons === null || dropdown == null || value === null || results === null)
            throw 'SiteLabelDropdown: Missing element(s)';
        
        let icon = this.getIcon();
        icon && icon.on("click", () => { this.showToggle(); });
        
        this._dropdown = dropdown;
        this._input = input;
        this._input.on("input", () => {
            this.setFilter();
        });

        this._dependencies = dependencies ? QueryJSON.deserialize<Array<IFilterDepencency>>(dependencies) : null;
        this._buttons = buttons;
        
        let addAction: IActionTypeRequest | null = metaData && metaData.addAction ? {
            type: "Request",
            actionLink: {
                actionInfo: metaData.addAction
            }
        } : null;
        let editAction: IActionTypeRequest | null = metaData && metaData.editAction ? {
            type: "Request",
            actionLink: {
                actionInfo: metaData.editAction
            }
        } : null;


        let addButton = new SiteActionButton(card, {
            icon: "add",
            title: this.getSiteConfig().strings["add"],
            action: addAction,
            isFocusable: false
        }).appendTo(this._buttons);
        if (addAction === null)
            addButton.disable();

        this._editButton = new SiteActionButton(card, {
            icon: "edit",
            title: this.getSiteConfig().strings["edit"],
            action: editAction,
            isFocusable: false
        }).appendTo(this._buttons);
        if (editAction === null)
            this._editButton.disable();

        let clearButton = new SiteButton({
            icon: "clear",
            title: this.getSiteConfig().strings["clear"]
        }).appendTo(this._buttons);
        clearButton.on("click", (e) => {
            this.clearValue(); 
            this._input.select();
            e.stop();
        });

        this._results = results;
        this._clickEvent = this._results.clickEvent.on(async a => {
            this.click(a.model);
            
            if(this.config.popout != "always")
                a.event.stop();
        });

        this._valueElement = value;

        let firstChild = this._valueElement.firstChild();
        this._valueTemplate = new QueryTemplate(firstChild && firstChild.getHtml() || "");
        firstChild && firstChild.remove();

        if (metaData !== undefined && metaData.entity !== undefined) {
            this.renderModel(metaData.entity);
        }

        if (this._dependencies && this._dependencies.length > 0) {
            // Subscribe for model changes
            this._changedEvent = this.getCard().Model.changedEvent.on(data => {
                // Get first item of path not equalling current path (so we make relative paths form an absolute one)
                for (var x = 0; x < Math.min(data.path.length, this.path.length); x++) {
                    if (this.path[x] != data.path[x])
                        break;
                }
                // And now join it
                let property = data.path.slice(x).join(".");
                if(this._dependencies && this._dependencies.some(a => a.crossReference == false && a.property == property)) {
                    this.clearValue();
                }

                this.setFilter();
            });
        }
    }
    init(elem: IQueryElement) {
        super.init(elem);

        // Set filter (only for first time)
        this.setFilter();

        // Set the popout state
        this._dropdown.addClass("popout-" + this.config.popout);
        
        if (this.config.popout == "always") {
            let onActive = this.getCard().on("qts.active", () => {
                this._results.start();

                this.getCard().off("qts.active", onActive);
            });
        }
    }
    getInput() {
        return this._input;
    }

    defaultKeyDownHandler(e: KeyboardEvent) {
        switch (e.keyCode) {
            case 13: // ENTER (stop SUPER)
                let enterSelector = this._results.getSelector();
                enterSelector && this.click(enterSelector);
                return;
            case 8: // Backspace
                if (this._input.getVal() == null || this._input.getVal() == "")
                    this.clearValue();
                break;
            case 9: // TAB
                let tabSelector = this._results.getSelector();
                tabSelector && this.click(tabSelector);
                break;    
            case 38: // KEY UP
                this._results.selectorUp();    
                break;
            case 40: // KEY DOWN
                this._results.selectorDown();    
                break;    
        }

        if (e.keyCode != 9)
            this.show();
        
        super.defaultKeyDownHandler(e);
    }
    defaultConfig(): ISiteLabelDropdownConfig {
        return {
            popout: "normal",
            filters: [],
            scroll: "top"
        }
    }
    show() {
        this._results.start();
        this._dropdown.addClass("show");
    }
    unShow() {
        this._dropdown.removeClass("show");
        this.clearInput();
    }
    showToggle() {
        if (this._dropdown.containsClass("show"))
            this.unShow();
        else
            this.show();
    }
    setFilter() {
        if (this._input.getVal())
            this.addClass("searching");
        else
            this.removeClass("searching");


        let filter:any = this._results.getFilter();
        filter.input = this._input.getVal() || "";

        // Relative to absolute path, get basePath:
        let basePath = this.path.slice(0, this.path.length - 1);

        // Set dependencies
        if (this._dependencies && this._dependencies.length > 0) {
            for (let item of this._dependencies) {
                let newValue = this.getCard().Model.get(
                    basePath.concat(SiteModel.split(item.property))
                );
                
                // Check if we've a filter
                if (newValue != null) {
                    if (item.isArray) {
                        filter[item.filterProperty] = Array.isArray(newValue) ?
                            newValue
                            : [newValue];
                    } else {
                        filter[item.filterProperty] = Array.isArray(newValue) ?
                            newValue[0]
                            : newValue;
                    }
                } else {
                    delete filter[item.filterProperty];
                }    
                
            }
        }

        this._results.setFilter(filter);
    }
    makeActive() {
        if (super.makeActive()) {
            if (!this.getSiteConfig().settings.includes("NoAutoPopout"))
                this.show();
            
            this._input.select();
            return true;
        }
        return false;
    }
    makeInactive() {
        super.makeInactive();
        this.removeClass("searching");
        this.unShow();
    }

    clearInput() {
        // Clear input (when needed)
        let input = this._input.getVal();
        
        // And set the filter (when there was input)
        if (input) {
            this._input.setVal("");
            this.setFilter();
        }
    }

    click(ev: ISiteHelperQueryRequestItem) {
        this.setValueModel(ev.getResult());
        this._input.select();
        this.makeNextActive();
    }

    clearValue() {
        this.setValueModel(null);
    }
    getValue() {
        return this._value;
    }
    setValue(value: any) {
        let modelData = this.getModelData() || {};

        if (value === null) {
            this._editButton.disable();
        } else {
            if (this._editButton.Action !== null) {
                this._editButton.enable();
                
                if (this._editButton.Action instanceof SiteActionRequest) {
                    let id :string;
                    if (modelData.isObjectReferenceCopy)
                        id = value.referenceId;
                    else
                        id = value;
                    
                    this._editButton.Action.getAction().actionLink.actionInfo.id = id;
                }
            }    
        }

        super.setValue(value);
        this._value = value;

        // When item is selected whe are done:
        this.unShow();
    }
    async remove(removeInner = true) {
        await super.remove(removeInner);

        // Dispose all resources
        if(this._results === undefined)
            return;
        
        if(this._clickEvent)
            this._results.clickEvent.off(this._clickEvent);
        if(this._changedEvent)
            this.getCard().Model.changedEvent.off(this._changedEvent);
    }

    renderModel(model: any) {
        this._valueElement.setHtml(
            this._valueTemplate.render(model)
        );
    }
    
    private setValueModel(model: any) {
        let modelData = this.getModelData() || {};
        
        if (model === null) {
            this.setValue(null);
            this._valueElement.setHtml("");
            delete modelData.entity;
        } else {
            if (modelData.isObjectReferenceCopy) {
                let reference: any = {
                    referenceId: model.id
                };

                this.setValue(reference);
            } else {
                this.setValue(model.id);
            }

            this.renderModel(model);
            modelData.entity = model;
        }
        
        this.setModelData(modelData);
    }

    static getTemplate(smallTemplate: string, largeTemplate: string, title: string, path: string, query: string) {
        let _ = QueryElement.QueryElement;
        return _("label")
            .setAttr("data-path", path)
            .setAttr("data-type", "dropdown")
            .append(_("i").addClass("inline").addClass("collapse").setHtml("keyboard_arrow_down"))
            .append(_("div").addClass("value")
                .append(_("span").setHtml(smallTemplate))
            )
            .append(_("input").setAttr("type", "text")
                .setAttr("data-query", query)
            )
            .append(_("span").addClass("label").setHtml(title))
            .append(_("div")
                .addClass("dropdown")
                .addClass("popout")
                .append(_("div").addClass("buttons"))
                .append(_("div").addClass("results")
                    .append(_("div").addClass("templates")
                        .append(_("div")
                            .addClass("list")
                            .setAttr("data-template", "default")
                            .setAttr("data-type", "list")
                            .setHtml(largeTemplate)))
                )
            )
    }
}